import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { resolveBallPose } from '@/lib/ball-director'
import { addTicker } from '@/lib/ticker'

/**
 * The one 3D pickleball that travels down the page. Sections steer it with setBallDirector.
 * three.js is loaded lazily so it never blocks the first paint.
 */
export const BallOverlay: FC = (): ReactNode => {
    const canvasRef = useRef<HTMLCanvasElement>(null)

    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas) return

        let disposed = false
        let cleanup: (() => void) | undefined

        import('@/lib/three/ball-renderer')
            .then(({ createBallRenderer }) => {
                if (disposed) return

                let ballRenderer: ReturnType<typeof createBallRenderer>

                try {
                    ballRenderer = createBallRenderer(canvas)
                }
                catch (error) {
                    console.error('The 3D ball could not start, the page works without it.', error)
                    return
                }

                const removeTicker = addTicker((time, delta) => {
                    const pose = resolveBallPose({ width: canvas.clientWidth, height: canvas.clientHeight, time })
                    ballRenderer.render(pose, delta)
                }, 100)

                cleanup = () => {
                    removeTicker()
                    ballRenderer.dispose()
                }
            })
            .catch((error) => console.error(error))

        return () => {
            disposed = true
            cleanup?.()
        }
    }, [])

    return (
        <canvas
            ref={canvasRef}
            aria-hidden='true'
            className='pointer-events-none fixed inset-x-0 top-0 z-40 h-screen w-full opacity-0'
        />
    )
}
